import * as THREE from 'three';
import { Chunk, chunkKey, worldToChunk } from './Chunk';
import { CHUNK_SIZE, WATER_LEVEL, sampleBiome, sampleHeight, riverStrength, type ResourceInstance, type WorldParams } from './WorldGen';
import { resourceDefs, type ResourceDef } from '../data/resources';
import { buildPieces, GRID, type BuildPieceDef } from '../data/build';
import { difficultyOf } from '../data/difficulty';
import { EventBus } from '../core/Events';
import { lambert } from './Assets';
import type { Settings } from '../core/Settings';
import { CommandBus, type Command, type CommandResult } from './Commands';

/**
 * Dünya cephesi (facade).
 * - Chunk streaming ve LOD
 * - Arazi yüksekliği sorguları (analitik: fizik için mesh gerekmez)
 * - KALICI DEĞİŞİKLİKLER: yalnızca oyuncunun yaptıkları saklanır
 *   (toplanan kaynaklar, kurulan yapılar, sandık içerikleri, keşfedilen chunk'lar).
 *   Arazi ve kaynak yerleşimi seed'den yeniden üretildiği için kaydedilmez.
 */

export interface PlacedStructure {
  id: string;
  piece: string;
  x: number;
  y: number;
  z: number;
  rotY: number;
  /** Kalan dayanıklılık. */
  hp: number;
  /** Sandık ise içerik. dur = alet/zırh örneğinin kalan dayanıklılığı (kaydedilir, kaybolmaz). */
  storage?: Array<{ item: string; count: number; dur?: number } | null>;
  /** Crop için ekim zamanı; diğer yapılarda tanımsızdır. */
  plantedAt?: number;
}

export interface HarvestRecord {
  /** Yenilenme zamanı (oyun saniyesi); 0 = kalıcı. */
  respawnAt: number;
}

export interface WorldSaveData {
  seed: number;
  mode: 'story' | 'normal';
  difficulty: string;
  name: string;
  harvested: Array<[string, number]>;
  structures: PlacedStructure[];
  discovered: string[];
}

type WorldEvents = {
  'chunk:loaded': { key: string };
  'chunk:unloaded': { key: string };
  'chunk:discovered': { key: string; total: number };
  'structure:placed': PlacedStructure;
  'structure:removed': { id: string };
  'resource:harvested': { id: string; def: string };
};

export class World {
  readonly events = new EventBus<WorldEvents>();
  readonly commands = new CommandBus();
  readonly scene = new THREE.Scene();
  readonly params: WorldParams;
  readonly name: string;

  private chunks = new Map<string, Chunk>();
  /** Toplanan kaynaklar: id -> yenilenme zamanı. */
  private harvested = new Map<string, number>();
  private structures = new Map<string, PlacedStructure>();
  private structureMeshes = new Map<string, THREE.Object3D>();
  private discovered = new Set<string>();
  private buildQueue: Chunk[] = [];
  private water: THREE.Mesh;
  private structureRoot = new THREE.Group();

  constructor(save: Pick<WorldSaveData, 'seed' | 'mode' | 'difficulty' | 'name'>, private settings: Settings) {
    this.name = save.name;
    this.params = {
      seed: save.seed >>> 0,
      mode: save.mode,
      difficulty: difficultyOf(save.difficulty),
      storyRadius: 640,
    };
    this.scene.add(this.structureRoot);

    // Su: oyuncuyu takip eden tek büyük düzlem (chunk başına su mesh'i yerine — çok daha ucuz)
    const waterGeo = new THREE.PlaneGeometry(1, 1);
    waterGeo.rotateX(-Math.PI / 2);
    this.water = new THREE.Mesh(waterGeo, new THREE.MeshLambertMaterial({ color: 0x2f7ba8, transparent: true, opacity: 0.82 }));
    this.water.position.y = WATER_LEVEL;
    this.water.renderOrder = -1;
    this.scene.add(this.water);
  }

  // ------------------------------------------------------------ komut darboğazı

  /**
   * Dünyayı değiştiren TEK giriş noktası.
   * Sistemler doğrudan mutasyon metodu çağırmaz; hepsi buradan geçer.
   * Çok oyunculu geldiğinde bu metot ağ üzerinden gelen komutları da uygular.
   */
  apply(cmd: Command): CommandResult {
    let result: CommandResult;
    switch (cmd.t) {
      case 'structure:place': {
        const def = buildPieces.get(cmd.piece);
        if (!def) result = { ok: false, reason: 'Bilinmeyen yapı parçası' };
        else {
          // Kimlik yalnızca ilk kez üretilir ve komuta yazılır; ağ üzerinden
          // giden komut aynı kimliği taşır, böylece tüm istemcilerde eşleşir.
          if (!cmd.id) cmd.id = `s${Date.now().toString(36)}${Math.floor(Math.random() * 1e6).toString(36)}`;
          const st = this.placeStructureRaw(def, cmd.x, cmd.y, cmd.z, cmd.rotY, cmd.id, cmd.storageSize);
          result = st ? { ok: true, data: st } : { ok: false, reason: 'Burada zaten bir yapı var' };
        }
        break;
      }
      case 'structure:remove': {
        const st = this.removeStructureRaw(cmd.id);
        result = st ? { ok: true, data: st } : { ok: false, reason: 'Yapı bulunamadı' };
        break;
      }
      case 'structure:replace': {
        const def = buildPieces.get(cmd.piece);
        const st = def ? this.replaceStructureRaw(cmd.id, def) : null;
        result = st ? { ok: true, data: st } : { ok: false, reason: 'Dönüştürülemedi' };
        break;
      }
      case 'structure:damage': {
        const destroyed = this.damageStructureRaw(cmd.id, cmd.amount);
        result = { ok: true, data: { destroyed } };
        break;
      }
      case 'structure:repair': {
        this.repairStructureRaw(cmd.id, cmd.amount);
        result = { ok: true };
        break;
      }
      case 'resource:harvest': {
        const def = resourceDefs.get(cmd.def);
        if (!def) result = { ok: false, reason: 'Bilinmeyen kaynak' };
        else {
          this.markHarvestedRaw(cmd.id, def, this.lastGameTime);
          result = { ok: true };
        }
        break;
      }
      // Canlı komutları CreatureManager tarafından dinlenir (dünya durumu tutmaz)
      case 'creature:spawn':
      case 'creature:damage':
      case 'creature:despawn':
        result = { ok: true };
        break;
      case 'chest:store': {
        const st = this.structures.get(cmd.id);
        if (!st || !st.storage) result = { ok: false, reason: 'Sandık yok' };
        else {
          if (cmd.slot < 0 || cmd.slot >= st.storage.length) result = { ok: false, reason: 'Geçersiz yuva' };
          else {
            st.storage[cmd.slot] = cmd.item ? { item: cmd.item, count: cmd.count, dur: cmd.dur } : null;
            result = { ok: true };
          }
        }
        break;
      }
      default:
        result = { ok: false, reason: 'Bilinmeyen komut' };
    }
    this.commands.emit(cmd, result);
    return result;
  }

  /** Son bilinen oyun saati (komutların zaman gerektiren işleri için). */
  private lastGameTime = 0;

  // ------------------------------------------------------------ arazi sorguları

  heightAt(x: number, z: number): number {
    return sampleHeight(this.params, x, z);
  }

  biomeAt(x: number, z: number) {
    return sampleBiome(this.params, x, z);
  }

  isWaterAt(x: number, z: number): boolean {
    return this.heightAt(x, z) < WATER_LEVEL;
  }

  isRiverAt(x: number, z: number): boolean {
    return riverStrength(this.params, x, z) > 0.3;
  }

  /** Yüzey normali (eğim kontrolü için) — 4 örnekli merkezi fark. */
  slopeAt(x: number, z: number): number {
    const d = 1.2;
    const hx = this.heightAt(x + d, z) - this.heightAt(x - d, z);
    const hz = this.heightAt(x, z + d) - this.heightAt(x, z - d);
    return Math.hypot(hx, hz) / (2 * d);
  }

  /** Oyuncunun bastığı yüzey: arazi ya da üstünde durulabilir yapı. */
  supportHeightAt(x: number, z: number, feetY: number): number {
    let best = this.heightAt(x, z);
    for (const s of this.structures.values()) {
      const def = buildPieces.get(s.piece);
      if (!def || !def.walkable) continue;
      if (Math.abs(x - s.x) > GRID / 2 || Math.abs(z - s.z) > GRID / 2) continue;
      const top = s.y + (def.shape === 'floor' || def.shape === 'ceiling' ? 0.2 : 0);
      if (top <= feetY + 1 && top > best) best = top;
    }
    return best;
  }

  /** Yapı çarpışması (yatay). */
  blockedAt(x: number, z: number, feetY: number, radius = 0.45): boolean {
    for (const s of this.structures.values()) {
      const def = buildPieces.get(s.piece);
      if (!def || !def.blocks) continue;
      if (feetY + 1.7 < s.y || feetY > s.y + 3) continue;
      const dx = x - s.x;
      const dz = z - s.z;
      const c = Math.cos(s.rotY);
      const sn = Math.sin(s.rotY);
      const lx = dx * c - dz * sn;
      const lz = dx * sn + dz * c;
      const halfL = def.shape === 'fence' ? GRID / 2 : GRID / 2;
      const halfT = def.shape === 'fence' ? 0.2 : 0.35;
      if (Math.abs(lx) <= halfL + radius && Math.abs(lz) <= halfT + radius) return true;
    }
    return false;
  }

  // ------------------------------------------------------------ chunk streaming

  /** Oyuncu konumuna göre chunk'ları yükler/boşaltır. Kare başına bütçeli çalışır. */
  update(playerX: number, playerZ: number, gameTime: number): void {
    this.lastGameTime = gameTime;
    const profile = this.settings.profile;
    const dist = profile.renderDistance;
    const { cx, cz } = worldToChunk(playerX, playerZ);

    // Yükleme: yakından uzağa
    const wanted: Array<{ cx: number; cz: number; d: number }> = [];
    for (let dz = -dist; dz <= dist; dz++) {
      for (let dx = -dist; dx <= dist; dx++) {
        const d = Math.hypot(dx, dz);
        if (d > dist + 0.5) continue;
        wanted.push({ cx: cx + dx, cz: cz + dz, d });
      }
    }
    wanted.sort((a, b) => a.d - b.d);

    for (const w of wanted) {
      const key = chunkKey(w.cx, w.cz);
      let chunk = this.chunks.get(key);
      if (!chunk) {
        chunk = new Chunk(w.cx, w.cz);
        this.chunks.set(key, chunk);
        this.scene.add(chunk.group);
        // Oyuncunun bastığı alan ve ilk komşu halkası kuyrukta kalırsa su
        // düzlemi terrain yokmuş gibi mavi yarık olarak görünür. Bu 3×3
        // yakın alanın arazisini aynı karede kur; uzak alanlar bütçeli kalır.
        if (w.d <= 1) {
          chunk.buildTerrain(this.params, profile.terrainSegments);
          if (w.d === 0) {
            chunk.buildResources(this.params, this.harvestedSet(gameTime), profile.vegetationDensity, profile.shadows);
          }
          this.markDiscovered(chunk.cx, chunk.cz);
          this.events.emit('chunk:loaded', { key: chunk.key });
        } else this.buildQueue.push(chunk);
      }
    }

    // Boşaltma: menzil + 2 dışındakiler (kalıcı değişiklikler kayıtta, kaybolmaz)
    for (const [key, chunk] of this.chunks) {
      const d = Math.hypot(chunk.cx - cx, chunk.cz - cz);
      if (d > dist + 2) {
        chunk.dispose();
        this.scene.remove(chunk.group);
        this.chunks.delete(key);
        const idx = this.buildQueue.indexOf(chunk);
        if (idx > -1) this.buildQueue.splice(idx, 1);
        this.events.emit('chunk:unloaded', { key });
      }
    }

    // Kare başına en fazla 2 chunk kur: mobilde takılma olmasın
    let budget = 2;
    while (budget > 0 && this.buildQueue.length > 0) {
      const chunk = this.buildQueue.shift()!;
      if (!this.chunks.has(chunk.key)) continue;
      const d = Math.hypot(chunk.cx - cx, chunk.cz - cz);
      // LOD: uzak chunk'lar daha az segment
      const seg = d <= 1 ? profile.terrainSegments : d <= dist * 0.6 ? Math.max(4, profile.terrainSegments >> 1) : Math.max(2, profile.terrainSegments >> 2);
      chunk.buildTerrain(this.params, seg);
      // Bitki yalnızca yakın chunk'larda
      if (d <= Math.max(2, dist - 1)) {
        chunk.buildResources(this.params, this.harvestedSet(gameTime), profile.vegetationDensity, profile.shadows);
      }
      this.markDiscovered(chunk.cx, chunk.cz);
      this.events.emit('chunk:loaded', { key: chunk.key });
      budget--;
    }

    // Su düzlemi oyuncuyu takip eder
    const waterSpan = (dist + 2) * CHUNK_SIZE * 2;
    this.water.scale.set(waterSpan, waterSpan, 1);
    this.water.position.x = playerX;
    this.water.position.z = playerZ;

    // Yenilenen kaynakları geri getir
    if (this.pendingRespawnCheck < gameTime) {
      this.pendingRespawnCheck = gameTime + 10;
      for (const [id, at] of this.harvested) {
        if (at > 0 && at <= gameTime) {
          this.harvested.delete(id);
          const chunk = this.chunkOfResourceId(id);
          chunk?.showResource(id);
        }
      }
    }
  }

  private pendingRespawnCheck = 0;

  private harvestedSet(gameTime: number): ReadonlySet<string> {
    const set = new Set<string>();
    for (const [id, at] of this.harvested) {
      if (at === 0 || at > gameTime) set.add(id);
    }
    return set;
  }

  private chunkOfResourceId(id: string): Chunk | undefined {
    const parts = id.split('_');
    const cx = Number(parts[0]);
    const cz = Number(parts[1]);
    if (!Number.isFinite(cx) || !Number.isFinite(cz)) return undefined;
    return this.chunks.get(chunkKey(cx, cz));
  }

  loadedChunks(): Chunk[] {
    return [...this.chunks.values()];
  }

  pickables(): THREE.Object3D[] {
    const out: THREE.Object3D[] = [];
    for (const c of this.chunks.values()) out.push(...c.pickables());
    out.push(this.structureRoot);
    return out;
  }

  // ------------------------------------------------------------ keşif

  private markDiscovered(cx: number, cz: number): void {
    const key = chunkKey(cx, cz);
    if (this.discovered.has(key)) return;
    this.discovered.add(key);
    this.events.emit('chunk:discovered', { key, total: this.discovered.size });
  }

  get discoveredCount(): number {
    return this.discovered.size;
  }

  isDiscovered(cx: number, cz: number): boolean {
    return this.discovered.has(chunkKey(cx, cz));
  }

  discoveredKeys(): string[] {
    return [...this.discovered];
  }

  // ------------------------------------------------------------ kaynak toplama

  resourceAt(id: string): { instance: ResourceInstance; def: ResourceDef } | null {
    const chunk = this.chunkOfResourceId(id);
    const inst = chunk?.resources.find((r) => r.id === id);
    if (!inst) return null;
    const def = resourceDefs.get(inst.def);
    if (!def) return null;
    return { instance: inst, def };
  }

  isHarvested(id: string, gameTime: number): boolean {
    const at = this.harvested.get(id);
    if (at === undefined) return false;
    return at === 0 || at > gameTime;
  }

  /** Kaynağı toplanmış işaretler; yenilenebilir ise zamanlayıcı kurar. */
  private markHarvestedRaw(id: string, def: ResourceDef, gameTime: number): void {
    this.harvested.set(id, def.respawn > 0 ? gameTime + def.respawn : 0);
    this.chunkOfResourceId(id)?.hideResource(id);
    this.events.emit('resource:harvested', { id, def: def.id });
  }

  // ------------------------------------------------------------ yapılar

  private placeStructureRaw(
    piece: BuildPieceDef,
    x: number,
    y: number,
    z: number,
    rotY: number,
    forcedId?: string,
    storageSize?: number,
  ): PlacedStructure | null {
    const id = forcedId ?? `s${Date.now().toString(36)}${Math.floor(Math.random() * 1e4).toString(36)}`;
    if (this.structures.has(id)) return null;   // aynı komut iki kez uygulanmasın
    const key = structureSlot(piece, x, y, z);
    for (const s of this.structures.values()) {
      const d = buildPieces.get(s.piece);
      if (d && structureSlot(d, s.x, s.y, s.z) === key) return null; // aynı hücre dolu
    }
    const st: PlacedStructure = { id, piece: piece.id, x, y, z, rotY, hp: piece.durability };
    if (piece.shape === 'crop') st.plantedAt = this.lastGameTime;
    // Ölüm mezarı gibi özel boyutlu sandıklar storageSize taşır; normal sandık sabit 24.
    if (piece.id === 'chest' || storageSize !== undefined) st.storage = new Array(storageSize ?? 24).fill(null);
    this.structures.set(id, st);
    this.addStructureMesh(st, piece);
    this.events.emit('structure:placed', st);
    return st;
  }

  private removeStructureRaw(id: string): PlacedStructure | null {
    const st = this.structures.get(id);
    if (!st) return null;
    this.structures.delete(id);
    const mesh = this.structureMeshes.get(id);
    if (mesh) {
      this.structureRoot.remove(mesh);
      disposeObject(mesh);
      this.structureMeshes.delete(id);
    }
    this.events.emit('structure:removed', { id });
    return st;
  }

  /** Çekiç ile kademe/varyant değiştirme: konumu koruyarak parça değiştirir. */
  private replaceStructureRaw(id: string, newPiece: BuildPieceDef): PlacedStructure | null {
    const old = this.structures.get(id);
    if (!old) return null;
    const mesh = this.structureMeshes.get(id);
    if (mesh) {
      this.structureRoot.remove(mesh);
      disposeObject(mesh);
      this.structureMeshes.delete(id);
    }
    old.piece = newPiece.id;
    old.hp = newPiece.durability;
    this.addStructureMesh(old, newPiece);
    return old;
  }

  structureById(id: string): PlacedStructure | null {
    return this.structures.get(id) ?? null;
  }

  allStructures(): PlacedStructure[] {
    return [...this.structures.values()];
  }

  /** Ekin olgunluğunu oyun zamanı üzerinden hesaplar; kayıt/yüklemede korunur. */
  cropProgress(st: PlacedStructure, gameTime = this.lastGameTime): number {
    return st.plantedAt === undefined ? 0 : Math.min(1, Math.max(0, (gameTime - st.plantedAt) / 180));
  }

  harvestCrop(id: string, gameTime = this.lastGameTime): { ok: boolean; piece?: string; reason?: string } {
    const st = this.structures.get(id);
    const def = st ? buildPieces.get(st.piece) : null;
    if (!st || !def || def.shape !== 'crop') return { ok: false, reason: 'Ekin yok' };
    if (this.cropProgress(st, gameTime) < 1) return { ok: false, reason: 'Ekin henüz büyüyor' };
    const piece = st.piece;
    this.removeStructureRaw(id);
    return { ok: true, piece };
  }

  private damageStructureRaw(id: string, amount: number): boolean {
    const st = this.structures.get(id);
    if (!st) return false;
    st.hp -= amount;
    if (st.hp <= 0) {
      this.removeStructureRaw(id);
      return true;
    }
    return false;
  }

  private repairStructureRaw(id: string, amount: number): void {
    const st = this.structures.get(id);
    const def = st ? buildPieces.get(st.piece) : null;
    if (!st || !def) return;
    st.hp = Math.min(def.durability, st.hp + amount);
  }

  private addStructureMesh(st: PlacedStructure, def: BuildPieceDef): void {
    const mesh = buildStructureMesh(def);
    mesh.position.set(st.x, st.y, st.z);
    mesh.rotation.y = st.rotY;
    mesh.userData.structureId = st.id;
    mesh.traverse((o) => {
      o.userData.structureId = st.id;
      if (o instanceof THREE.Mesh) {
        o.castShadow = this.settings.profile.shadows;
        o.receiveShadow = true;
      }
    });
    this.structureRoot.add(mesh);
    this.structureMeshes.set(st.id, mesh);
  }

  // ------------------------------------------------------------ kayıt

  toSave(): WorldSaveData {
    return {
      seed: this.params.seed,
      mode: this.params.mode,
      difficulty: this.params.difficulty.id,
      name: this.name,
      harvested: [...this.harvested.entries()],
      structures: this.allStructures().map((st) => ({
        ...st,
        storage: st.storage?.map((slot) => (slot ? { ...slot } : null)),
      })),
      discovered: [...this.discovered],
    };
  }

  loadSave(data: WorldSaveData): void {
    this.harvested.clear();
    for (const entry of data.harvested ?? []) {
      if (!Array.isArray(entry)) continue;
      const [id, at] = entry;
      if (typeof id === 'string' && Number.isFinite(at)) this.harvested.set(id, at);
    }
    this.discovered = new Set((data.discovered ?? []).filter((k) => typeof k === 'string'));

    for (const id of [...this.structures.keys()]) this.removeStructureRaw(id);
    for (const st of data.structures ?? []) {
      const def = buildPieces.get(st?.piece);
      if (!def || !Number.isFinite(st.x) || !Number.isFinite(st.y) || !Number.isFinite(st.z)) continue;
      const clean: PlacedStructure = {
        id: typeof st.id === 'string' ? st.id : `s${Math.random().toString(36).slice(2)}`,
        piece: def.id,
        x: st.x,
        y: st.y,
        z: st.z,
        rotY: Number.isFinite(st.rotY) ? st.rotY : 0,
        hp: Number.isFinite(st.hp) ? Math.min(st.hp, def.durability) : def.durability,
      };
      if (Number.isFinite(st.plantedAt)) clean.plantedAt = st.plantedAt;
      if (Array.isArray(st.storage)) clean.storage = st.storage.map((slot) => slot ? { ...slot } : null);
      this.structures.set(clean.id, clean);
      this.addStructureMesh(clean, def);
    }
  }

  dispose(): void {
    for (const c of this.chunks.values()) c.dispose();
    this.chunks.clear();
    for (const id of [...this.structureMeshes.keys()]) {
      const mesh = this.structureMeshes.get(id)!;
      disposeObject(mesh);
      this.structureMeshes.delete(id);
    }
    this.water.geometry.dispose();
    this.scene.clear();
  }
}

/** Izgara hücresi kimliği: aynı yere iki yapı konmasın. */
function structureSlot(def: BuildPieceDef, x: number, y: number, z: number): string {
  return `${def.shape}|${Math.round(x * 2) / 2}|${Math.round(y * 2) / 2}|${Math.round(z * 2) / 2}`;
}

/** Yapı parçası için düşük poligonlu mesh. */
export function buildStructureMesh(def: BuildPieceDef): THREE.Object3D {
  const group = new THREE.Group();
  const color = def.tierId.startsWith('stone') ? 0x8f8f8f : 0x9a6b3f;
  const accent = def.tierId.endsWith('_r') ? 0x5a4632 : color;
  const mat = lambert(color);
  const matAccent = lambert(accent);
  const T = 0.25;

  const add = (w: number, h: number, d: number, x: number, y: number, z: number, m: THREE.Material = mat) => {
    const mesh = new THREE.Mesh(boxGeo(w, h, d), m);
    mesh.position.set(x, y, z);
    group.add(mesh);
    return mesh;
  };

  switch (def.shape) {
    case 'floor':
    case 'ceiling':
      add(GRID, T, GRID, 0, T / 2, 0);
      break;
    case 'wall': {
      if (def.variant === 'solid') {
        add(GRID, 3, T, 0, 1.5, 0);
      } else if (def.variant === 'window') {
        add(GRID, 1, T, 0, 0.5, 0);
        add(GRID, 0.9, T, 0, 2.55, 0);
        add(0.3, 1.6, T, -GRID / 2 + 0.15, 1.8, 0, matAccent);
        add(0.3, 1.6, T, GRID / 2 - 0.15, 1.8, 0, matAccent);
      } else {
        // kapı: üst kiriş + iki yan sütun, ortası geçilebilir
        add(GRID, 0.6, T, 0, 2.7, 0);
        add(0.7, 2.4, T, -GRID / 2 + 0.35, 1.2, 0, matAccent);
        add(0.7, 2.4, T, GRID / 2 - 0.35, 1.2, 0, matAccent);
      }
      break;
    }
    case 'ramp': {
      const mesh = new THREE.Mesh(rampGeo(), mat);
      group.add(mesh);
      break;
    }
    case 'stair': {
      const steps = 5;
      for (let i = 0; i < steps; i++) {
        add(GRID, 3 / steps, GRID / steps, 0, (3 / steps) * (i + 0.5), -GRID / 2 + (GRID / steps) * (i + 0.5));
      }
      break;
    }
    case 'fence':
      add(GRID, 0.16, 0.16, 0, 1.1, 0, matAccent);
      add(GRID, 0.16, 0.16, 0, 0.6, 0, matAccent);
      for (let i = -1; i <= 1; i++) add(0.18, 1.3, 0.18, (i * GRID) / 2.2, 0.65, 0);
      break;
    case 'roof': {
      const mesh = new THREE.Mesh(roofGeo(), mat);
      group.add(mesh);
      break;
    }
    case 'station': {
      if (def.id === 'workbench') {
        add(2.2, 0.2, 1.6, 0, 1.05, 0, lambert(0x8d6e46));
        for (const [sx, sz] of [[-1, -0.7], [1, -0.7], [-1, 0.7], [1, 0.7]] as const) add(0.2, 1, 0.2, sx, 0.5, sz, lambert(0x5d4a30));
        add(0.5, 0.12, 0.5, 0.6, 1.2, 0, lambert(0x777777));
      } else if (def.id === 'furnace') {
        add(1.6, 1.8, 1.6, 0, 0.9, 0, lambert(0x777777));
        add(0.8, 0.7, 0.1, 0, 0.6, 0.82, lambert(0x2a2a2a));
      } else if (def.id === 'grinder') {
        add(1.6, 1.2, 1.6, 0, 0.6, 0, lambert(0x8a8a8a));
        const wheel = new THREE.Mesh(new THREE.CylinderGeometry(0.7, 0.7, 0.25, 12), lambert(0x666666));
        wheel.rotation.z = Math.PI / 2;
        wheel.position.set(0, 1.4, 0);
        group.add(wheel);
      } else {
        add(1.4, 0.9, 1, 0, 0.45, 0, lambert(0x8d6e46));
        add(1.45, 0.15, 1.05, 0, 0.95, 0, lambert(0x5d4a30));
      }
      break;
    }
    case 'crop': {
      add(1.6, 0.1, 1.6, 0, 0.05, 0, lambert(0x4a3b2a));
      for (let i = 0; i < 4; i++) {
        const blade = new THREE.Mesh(new THREE.ConeGeometry(0.12, 0.6, 4), lambert(def.id === 'crop_berry' ? 0x4b7d3a : 0xc8b45a));
        blade.position.set(-0.4 + (i % 2) * 0.8, 0.35, -0.4 + Math.floor(i / 2) * 0.8);
        group.add(blade);
      }
      break;
    }
  }
  return group;
}

const boxCache = new Map<string, THREE.BoxGeometry>();
function boxGeo(w: number, h: number, d: number): THREE.BoxGeometry {
  const k = `${w}_${h}_${d}`;
  let g = boxCache.get(k);
  if (!g) {
    g = new THREE.BoxGeometry(w, h, d);
    boxCache.set(k, g);
  }
  return g;
}

let _rampGeo: THREE.BufferGeometry | null = null;
function rampGeo(): THREE.BufferGeometry {
  if (_rampGeo) return _rampGeo;
  const h = 3;
  const s = GRID / 2;
  const g = new THREE.BufferGeometry();
  // Basit üçgen prizma
  const v = new Float32Array([
    -s, 0, s, s, 0, s, s, 0, -s, -s, 0, -s, // taban
    -s, h, -s, s, h, -s, // üst kenar
  ]);
  g.setAttribute('position', new THREE.BufferAttribute(v, 3));
  g.setIndex([0, 1, 2, 0, 2, 3, 3, 2, 5, 3, 5, 4, 0, 4, 5, 0, 5, 1, 0, 3, 4, 1, 5, 2]);
  g.computeVertexNormals();
  _rampGeo = g;
  return g;
}

let _roofGeo: THREE.BufferGeometry | null = null;
function roofGeo(): THREE.BufferGeometry {
  if (_roofGeo) return _roofGeo;
  const s = GRID / 2;
  const g = new THREE.BufferGeometry();
  const v = new Float32Array([-s, 0, s, s, 0, s, s, 0, -s, -s, 0, -s, 0, 1.6, 0]);
  g.setAttribute('position', new THREE.BufferAttribute(v, 3));
  g.setIndex([0, 1, 4, 1, 2, 4, 2, 3, 4, 3, 0, 4, 0, 3, 2, 0, 2, 1]);
  g.computeVertexNormals();
  _roofGeo = g;
  return g;
}

export function disposeObject(obj: THREE.Object3D): void {
  obj.traverse((o) => {
    if (o instanceof THREE.Mesh) {
      // Geometri/materyal paylaşımlı olabilir; yalnızca tekil olanları at.
      if (!boxGeoIsShared(o.geometry)) o.geometry.dispose();
    }
  });
}

function boxGeoIsShared(geo: THREE.BufferGeometry): boolean {
  for (const g of boxCache.values()) if (g === geo) return true;
  return geo === _rampGeo || geo === _roofGeo;
}

export { CHUNK_SIZE, WATER_LEVEL, chunkKey, worldToChunk };
